import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';
import { useHandlers } from '../../../context/page-context';
import {
  type Setting,
  type Settings,
  useSettings,
  useUpdateSettings,
} from '../../../context/settings-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { isServerError } from '../../../utils/errors';

const PAGE = 'features:storefront_options.pages.storefront_options_page' satisfies I18nPrefix;

/** The site settings shown on this page, with the fallback used when the API hasn't set one. */
const DEFAULTS = {
  applyPriceBreaksAcrossVariants: true,
  cartSearchType: 'full',
  hideAICartFeatures: false,
  paymentVisibilityEnabled: false,
  purchaseTimelineEnabled: false,
} as const satisfies Partial<Settings>;

type SiteSettingKey = keyof typeof DEFAULTS;
type SiteSettingValues = Pick<Settings, SiteSettingKey>;

const KEYS = Object.keys(DEFAULTS) as SiteSettingKey[];

export type UseSiteSettingsDraft = {
  values: SiteSettingValues;
  set: <K extends SiteSettingKey>(key: K, value: SiteSettingValues[K]) => void;
  reset: () => void;
  /** Stages the fallback values as edits, so they only take effect once the page is saved. */
  resetToDefaults: () => void;
  /** `notify` announces success - suppressed when the options draft save will announce it instead. */
  save: (options?: { notify?: boolean }) => void;
  isDirty: boolean;
  isAtDefaults: boolean;
  isSaving: boolean;
};

export function useSiteSettingsDraft(): UseSiteSettingsDraft {
  const { notificationHandler } = useHandlers();
  const settings = useSettings();
  const { mutateAsync } = useUpdateSettings();

  const [drafts, setDrafts] = useState<Partial<SiteSettingValues>>({});
  const [isSaving, setIsSaving] = useState(false);

  const saved = Object.fromEntries(
    KEYS.map((key) => [key, settings[key] ?? DEFAULTS[key]]),
  ) as SiteSettingValues;

  const values = Object.fromEntries(
    KEYS.map((key) => [key, drafts[key] ?? saved[key]]),
  ) as SiteSettingValues;

  const changed = KEYS.filter((key) => values[key] !== saved[key]);
  const isDirty = changed.length > 0;
  const isAtDefaults = KEYS.every((key) => values[key] === DEFAULTS[key]);

  // Read through a ref so `save` stays stable while `values`/`changed` are rebuilt each render.
  const pendingRef = useRef<Setting[]>([]);
  // Key and value can't be correlated across the `Setting` union generically.
  pendingRef.current = changed.map((key) => ({ key, value: values[key] }) as Setting);

  const set = useCallback<UseSiteSettingsDraft['set']>(
    (key, value) => setDrafts((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const reset = useCallback(() => setDrafts({}), []);

  const resetToDefaults = useCallback(() => setDrafts({ ...DEFAULTS }), []);

  const save = useCallback<UseSiteSettingsDraft['save']>(
    ({ notify = true } = {}) => {
      const pending = pendingRef.current;
      if (!pending.length || isSaving) return;

      setIsSaving(true);
      Promise.all(pending.map((setting) => mutateAsync(setting)))
        .then(() => {
          setDrafts({});
          if (notify) notificationHandler.notify(t('notifications:saved'), undefined, 'success');
        })
        .catch((error: unknown) => {
          if (isServerError(error)) return;
          notificationHandler.notify(t(`${PAGE}.site_settings_save_error`), undefined, 'error');
        })
        .finally(() => setIsSaving(false));
    },
    [isSaving, mutateAsync, notificationHandler],
  );

  return { values, set, reset, resetToDefaults, save, isDirty, isAtDefaults, isSaving };
}
